/**
 * Activation shortcuts — one helper per event, positional args, sensible defaults.
 * Underlying class accessible via `.client`.
 */
import { Activation } from "../../features/core/activation";

export class ActivationTracker {
  constructor(public readonly client: Activation) {}

  /** New user signup. */
  signup(userId: string, method: string, source?: string, props?: Record<string, unknown>) {
    return this.client.trackSignup(userId, method, source, props);
  }

  /** Returning-user login. */
  login(userId: string, method?: string, props?: Record<string, unknown>) {
    return this.client.trackLogin(userId, method, props);
  }

  /** Active-user ping for identity-based DAU/WAU/MAU (see `fg.pingActiveUser`). */
  trackActiveUser(userId: string, traits?: { email?: string; name?: string } & Record<string, unknown>) {
    return this.client.trackActiveUser(userId, traits);
  }

  /** Step in an onboarding flow (e.g. `step 2 of 5`). */
  onboardingStep(
    userId: string,
    stepId: string,
    stepName: string,
    stepOrder: number,
    totalSteps: number,
    extra?: { skipped?: boolean; timeSpent?: number; properties?: Record<string, unknown> }
  ) {
    return this.client.trackOnboardingStep({
      userId,
      stepId,
      stepName,
      stepOrder,
      totalSteps,
      ...extra,
    });
  }

  /** All onboarding steps finished. */
  onboardingComplete(userId: string, totalTimeSpent: number, stepsCompleted: number, stepsSkipped = 0) {
    return this.client.completeOnboarding(userId, totalTimeSpent, stepsCompleted, stepsSkipped);
  }

  /** First meaningful action after signup ("aha" moment). */
  firstAction(userId: string, actionName: string, timeSinceSignup: number, properties?: Record<string, unknown>) {
    return this.client.trackFirstAction({ userId, actionName, timeSinceSignup, properties });
  }

  /** Generic activation event. */
  event(eventName: string, properties: Record<string, unknown> = {}) {
    return this.client.create({ eventName, properties });
  }

}
